// Server URL validation with Flutter's exact messages
// (lib/security/connection_endpoint_validator.dart:16-110).

export type ServerScheme = 'https://' | 'http://';

export type EndpointValidation =
  | { ok: true; url: string; isHttp: boolean }
  | { ok: false; error: string };

const SCHEME_PREFIX = /^[A-Za-z][A-Za-z0-9+\-.]*:\/\//;

/** Validates a raw server URL. A lone trailing `/` is removed; sub-paths (`/abs`) are kept. */
export function validateServerUrl(raw: string): EndpointValidation {
  const input = raw.trim();
  if (!input) return { ok: false, error: 'Server URL is required.' };
  if (/\s/.test(input)) return { ok: false, error: 'Server URL cannot contain spaces.' };
  let url: URL;
  try {
    if (!SCHEME_PREFIX.test(input)) throw new Error('no scheme');
    url = new URL(input);
  } catch {
    return { ok: false, error: 'Enter a valid server URL.' };
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return { ok: false, error: 'Only http:// and https:// server URLs are supported.' };
  }
  if (!url.hostname) return { ok: false, error: 'Server URL must include a host name or IP address.' };
  if (url.username || url.password) return { ok: false, error: 'Do not include credentials in the server URL.' };
  if (url.search || url.hash || /[?#]/.test(input)) {
    return { ok: false, error: 'Remove query parameters and fragments from the server URL.' };
  }
  const path = url.pathname === '/' ? '' : url.pathname.replace(/\/+$/, '');
  return { ok: true, url: `${url.protocol}//${url.host}${path}`, isHttp: url.protocol === 'http:' };
}

/**
 * Validates onboarding / Add Connection input: the scheme picker value plus
 * the typed address. An address that already has a scheme is used as typed.
 */
export function validateOnboardingInput(scheme: string, rawInput: string): EndpointValidation {
  const normalizedScheme = scheme.trim().toLowerCase();
  if (normalizedScheme !== 'https://' && normalizedScheme !== 'http://' && normalizedScheme !== 'https' && normalizedScheme !== 'http') {
    return { ok: false, error: 'Invalid protocol selection.' };
  }
  const input = rawInput.trim();
  if (!input) return { ok: false, error: 'Server URL is required.' };
  const prefix = normalizedScheme.endsWith('://') ? normalizedScheme : `${normalizedScheme}://`;
  return validateServerUrl(SCHEME_PREFIX.test(input) ? input : prefix + input);
}
